import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

import { Button, DialogContent } from '@mui/material'

import { get } from 'utils/lodash'
import { sortByDateSelector } from 'data/utils/sortByDateSelector'
import {
  useGetRentalFeesResetHistoryQuery,
  useGetRentalFeesByConfiguredServiceQuery,
} from 'api/configured-service/configuredServiceRentalFeesCrud'
import { useGetAllRentalPeriodVersionsQuery, useGetRentalFeeToConfiguredServiceVersionsQuery } from 'api/entity-version/entityVersionCrud'

import T from 'T'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'
import HHDialogActions from 'components/common/HHDialogActions'
import GracePeriodHistory from './GracePeriodHistory'
import { getRows, normalizeAllPeriodVersionsToRows, normalizePriceChangeVersionsToRows } from './utils'

const GracePeriodResetHistoryDialog = ({
  isOpen = false,
  onClose,
  rows: controlledRows,
  isLoading: controlledLoading,
  configuredServiceId,
}) => {
  const isControlled = controlledRows !== undefined && controlledLoading !== undefined
  const shouldFetch = !isControlled && isOpen && !!configuredServiceId

  const { isFetching: isResetLoading, data: resetData } = useGetRentalFeesResetHistoryQuery(
    { id: configuredServiceId },
    { skip: !shouldFetch }
  )

  const { data: rentalFeeData } = useGetRentalFeesByConfiguredServiceQuery({ id: configuredServiceId }, { skip: !shouldFetch })
  const rentalFeeToConfiguredServiceId = get(rentalFeeData, 'configuredRentalFees[0].id')

  const { isFetching: isPeriodVersionsLoading, data: allPeriodVersionsData } = useGetAllRentalPeriodVersionsQuery(
    { rentalFeeToConfiguredServiceId },
    { skip: !shouldFetch || !rentalFeeToConfiguredServiceId }
  )
  const { isFetching: isPriceChangeLoading, data: priceChangeVersionsData } = useGetRentalFeeToConfiguredServiceVersionsQuery(
    { rentalFeeToConfiguredServiceId },
    { skip: !shouldFetch || !rentalFeeToConfiguredServiceId, refetchOnMountOrArgChange: true }
  )

  const graceRows = useMemo(() => getRows(resetData).map(row => ({ ...row, type: 'grace_reset' })), [resetData])
  const periodVersionRows = useMemo(() => normalizeAllPeriodVersionsToRows(allPeriodVersionsData), [allPeriodVersionsData])
  const priceChangeRows = useMemo(() => normalizePriceChangeVersionsToRows(priceChangeVersionsData), [priceChangeVersionsData])
  const sortedRows = useMemo(() => {
    const merged = [...graceRows, ...periodVersionRows, ...priceChangeRows]
    return sortByDateSelector({ data: merged, key: 'createdAt' })
  }, [graceRows, periodVersionRows, priceChangeRows])

  const rows = isControlled ? controlledRows : sortedRows
  const isLoading = isControlled ? controlledLoading : isResetLoading || isPeriodVersionsLoading || isPriceChangeLoading

  return (
    <HHBaseDialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <HHDialogTitle title={T.RENTAL_PERIOD_ACTIVITY} onClose={onClose} />
      <DialogContent sx={{ p: 0 }}>
        <GracePeriodHistory isLoading={isLoading} rows={rows} sx={{ px: 3 }} />
      </DialogContent>
      <HHDialogActions>
        <Button size="small" variant="contained" onClick={onClose}>
          {T.CLOSE}
        </Button>
      </HHDialogActions>
    </HHBaseDialog>
  )
}

GracePeriodResetHistoryDialog.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  rows: PropTypes.array,
  isLoading: PropTypes.bool,
  configuredServiceId: PropTypes.string,
}

export default GracePeriodResetHistoryDialog
